/**
 * Simplified UndoRedo system types for basic undo/redo functionality
 */

export type UndoRedoCallable = () => void | Promise<void>;

// Kept for backward compatibility — Silent is still useful; Primary is the default standalone action.
// Secondary is deprecated: use beginGroup/endGroup instead.
export const UndoRedoActionType = {
  Primary: 'primary',
  Secondary: 'secondary', // @deprecated — use beginGroup/endGroup for batching
  Silent: 'silent',
} as const;
export type UndoRedoActionType = (typeof UndoRedoActionType)[keyof typeof UndoRedoActionType];

export interface UndoRedoAction {
  name: string;
  actionType: UndoRedoActionType;

  // Do operations (executed on commit and redo)
  doMethod?: UndoRedoCallable;

  // Undo operations (executed on undo)
  undoMethod?: UndoRedoCallable;
}

/** A named group of one or more actions that undo/redo as a single step. */
export interface UndoRedoGroup {
  name: string;
  actions: UndoRedoAction[];
}

export interface IUndoRedoManager {
  /**
   * Records and executes a single undo/redo action in one call.
   * Preferred over the `createAction` / `setDoMethod` / `setUndoMethod` / `commitAction` sequence.
   *
   * @param name - Human-readable label shown in undo/redo toasts and history.
   * @param doMethod - Executed on commit (and again on redo). Must contain all stateful changes.
   * @param undoMethod - Executed on undo. Should fully reverse the effects of `doMethod`.
   * @param actionType - Defaults to `Primary`. Pass `Silent` to execute without entering history.
   */
  recordAction(
    name: string,
    doMethod: UndoRedoCallable,
    undoMethod: UndoRedoCallable,
    actionType?: UndoRedoActionType,
  ): Promise<void>;

  // Core action management
  /** @deprecated Use `recordAction` instead. */
  createAction(name: string, actionType?: UndoRedoActionType): void;
  /** @deprecated Use `recordAction` instead. */
  commitAction(execute?: boolean): void;

  // Grouping — all actions committed between beginGroup/endGroup undo/redo as one step
  beginGroup(name: string): void;
  endGroup(): void;
  /** Whether a group is currently open (i.e. between beginGroup and endGroup). */
  readonly isGroupOpen: boolean;

  // Do operations
  /** @deprecated Use `recordAction` instead. */
  setDoMethod(callable: UndoRedoCallable): void;

  // Undo operations
  /** @deprecated Use `recordAction` instead. */
  setUndoMethod(callable: UndoRedoCallable): void;

  // History navigation
  undo(): Promise<boolean>;
  redo(): Promise<boolean>;

  // State queries
  hasUndo(): boolean;
  hasRedo(): boolean;
  isCommittingAction(): boolean;
  /** True only while undo/redo is replaying history (not on initial action commit). */
  isReplayingHistory(): boolean;

  // History management
  clearHistory(increaseVersion?: boolean): void;
  getHistoryCount(): number;
  getCurrentAction(): number;
  getCurrentActionName(): string;
  getActionName(id: number): string;
  getVersion(): number;

  // Max steps limit
  maxSteps: number;
}
